import React, { useEffect, useState, useRef } from 'react';
import { Box, Checkbox, Typography, Popper, Radio } from '@mui/material';
import { HuePicker, RGBColor } from 'react-color';
import useAppContext from '../../../../hooks/useAppContext/useAppContext';
import usePractitionerControlsContext from '../../../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import BackendService from '../../../../services/BackendService';
import { generateColorString } from '../../../../utils/serializer';
import TaskScene from '../../../PhaserApp/components/application/scene/TaskScene';
import { colourDetailsStyles } from './style';
import { getResponsiveSize } from '../../../../utils';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { defaultBallColors } from '../../../../commands/practitioner/ballColors/BallColors';

interface ColourDetailsProps {
  autoColour: number;
  setColorOption: (value: number) => void;
  shouldTriggerEffect: boolean;
  title: string;
  taskHasStarted: boolean;
}

export function ColourDetails(props: ColourDetailsProps) {
  const { shouldTriggerEffect, autoColour, setColorOption, title } = props;
  const { sessionId, treatmentId } = useAppContext();
  const { game } = usePractitionerControlsContext();
  const { colourDetails } = colourDetailsStyles;

  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);

  const [color, setColor] = useState<RGBColor>({ r: 184, g: 84, b: 80 });
  const autoColorSelected = 1;
  const containerRef = useRef<HTMLDivElement>(null);
  const rgbToCss = ({ r, g, b }: RGBColor) => `rgb(${r}, ${g}, ${b})`;
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    const checkBounds = () => {
      if (!containerRef.current || !anchorEl) return;
      const bounds = containerRef.current.getBoundingClientRect();
      const anchor = anchorEl.getBoundingClientRect();
      if (
        anchor.bottom < bounds.top ||
        anchor.top > bounds.bottom ||
        anchor.left > bounds.right ||
        anchor.right < bounds.left
      ) {
        setDisplayColorPicker(false);
      }
    };

    window.addEventListener('scroll', checkBounds, true);
    window.addEventListener('resize', checkBounds);
    return () => {
      window.removeEventListener('scroll', checkBounds, true);
      window.removeEventListener('resize', checkBounds);
    };
  }, [anchorEl]);

  useEffect(() => {
    if (!shouldTriggerEffect) return;
    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      grabLatestColor();
    }, 200);

    return () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [color, shouldTriggerEffect]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      handleClose(event);
    };

    if (displayColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [displayColorPicker, anchorEl]);

  const grabLatestColor = () => {
    const colorString = `rgb(${color.r}, ${color.g}, ${color.b})`;
    const variablesInCommand = colorString;
    const finalCommand = generateColorString(colorString);
    const therapist = game?.scene.keys.therapist as TaskScene;
    BackendService.sendManualColorCommand(sessionId, treatmentId, variablesInCommand);
    therapist.updateTaskBasedOnEncodedCommand(finalCommand);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setDisplayColorPicker(prev => !prev);
  };

  const handleClose = (event: MouseEvent) => {
    if (
      popperRef.current &&
      anchorEl &&
      !popperRef.current.contains(event.target as Node) &&
      !anchorEl.contains(event.target as Node)
    ) {
      setDisplayColorPicker(false);
    }
  };

  const rgbToHsl = (r: number, g: number, b: number) => {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h = 0,
      s = 0,
      l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        default:
          h = (r - g) / d + 4;
      }
      h /= 6;
    }
    return { h, s, l };
  };

  const hslToRgb = (h: number, s: number, l: number) => {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    let r: number, g: number, b: number;
    if (s === 0) {
      r = g = b = l;
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }
    return {
      r: Math.round(r * 255),
      g: Math.round(g * 255),
      b: Math.round(b * 255),
    };
  };

  const mellow = (rgb: RGBColor, sFactor = 0.85, lFactor = 0.9): RGBColor => {
    const { h, s, l } = rgbToHsl(rgb.r, rgb.g, rgb.b);
    return hslToRgb(h, s * sFactor, l * lFactor);
  };

  const handleChangePicker = ({ rgb }: { rgb: RGBColor }) => {
    setColor(mellow(rgb));
  };

  const handleRadioChange = (rgb: RGBColor) => {
    setColor(rgb);
  };

  const autoColorHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setColorOption(event.target.checked ? 1 : 0);
  };

  return (
    <Box sx={colourDetails}>
      <Box className="subtitle-container">
        <Checkbox checked={autoColour === 1} onChange={autoColorHandler} />
        <Typography className="subtitle-text">{title}</Typography>
      </Box>
      <Box className="colors-container">
        {defaultBallColors.map(({ label, rgb }) => (
          <Radio
            disabled={autoColour === autoColorSelected}
            key={label}
            className="balls"
            onChange={() => handleRadioChange(rgb)}
            checked={color.r === rgb.r && color.g === rgb.g && color.b === rgb.b}
            sx={{
              color: label,
              paddingRight: '12px',
              '& .css-hyxlzm': {
                borderRadius: '50%',
                backgroundColor: label,
              },
              '&.Mui-checked': {
                color: 'transparent',
              },
              '&.Mui-disabled': {
                '& .css-hyxlzm': {
                  opacity: 0.5,
                },
                color: 'transparent',
              },
              '&:hover': {
                backgroundColor: label,
                opacity: 0.8,
                '& .css-hyxlzm': {
                  backgroundColor: label,
                  opacity: 0.8,
                },
              },
            }}
          />
        ))}
        <Radio
          disabled={autoColour === autoColorSelected}
          key="custom-color"
          className={`balls color-picker ${autoColour === autoColorSelected ? 'auto-colour-active' : ''}`}
          onClick={handleClick}
          icon={<EditOutlinedIcon />}
          checkedIcon={<EditOutlinedIcon />}
        />
        <Popper
          sx={{ paddingTop: getResponsiveSize(5), ml: getResponsiveSize(5), zIndex: 0, position: 'relative' }}
          open={displayColorPicker && autoColour === 0}
          anchorEl={anchorEl}
          placement="bottom-start"
          className="popper"
          ref={containerRef}
        >
          <Box
            className="popover"
            ref={popperRef}
            sx={{
              borderRadius: getResponsiveSize(12),
              boxShadow: 3,
              '& .hue-picker > div': {
                cursor: 'pointer',
              },
              '.hue-picker .hue-horizontal': {
                borderRadius: '12px !important',
                filter: 'saturate(85%) brightness(90%)',
              },
              '& .hue-picker > div > div > div > div': {
                width: `${getResponsiveSize(20)} !important`,
                height: `${getResponsiveSize(20)} !important`,
                backgroundColor: '#EAE8E4 !important',
                transform: `translate(-50%, -12%) !important`,
                top: '50% !important',
              },
            }}
          >
            <HuePicker
              className="hue-picker"
              width={getResponsiveSize(200)}
              height={getResponsiveSize(14)}
              color={color}
              onChange={handleChangePicker}
            />
          </Box>
        </Popper>
      </Box>
    </Box>
  );
}
